import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, RouterStateSnapshot, Routes } from "@angular/router";
import { LOGIN_ROUTES } from "../components/login-v4/login-v4.routing";
import { MedicalHistoryCompletedComponent } from "@apps/patient-app/src/app/my-dental/medical-history/medical-history-completed/medical-history-completed.component";
import { NavigationService } from "@apps/patient-core/src/app/services/navigation.service";
import cloneDeep from "lodash/cloneDeep";
import { loadComponentWithErrorHandling } from "@apps/patient-core/src/app/utils/routing";
import { ACCOUNT_ROUTES } from "../my-dental/account/account.routing";
import { CHECK_IN_ROUTES } from "../my-dental/check-in/check-in.routing";
import { DEPOSITS_ROUTES, DEPOSITS_ROUTE_DEFAULTS } from "../my-dental/deposits/deposits.routing";
import { BaseAuthService } from "@apps/patient-core/src/app/services/abstracts/auth.service";
import { BASE_MEDICAL_HISTORY_ROUTES } from "@apps/patient-core/src/app/my-dental/medical-history/medical-history.routing";
import { Constants } from "@apps/patient-app/constants";
import { MedicalHistoryAuthGuard } from "../my-dental/guards/medical-history.authguard";

@Injectable({
  providedIn: "root",
})
export class RedirectGuard {
  canActivate(): boolean {
    return false;
  }
}

@Injectable({
  providedIn: "root",
})
export class RestrictedAuthGuard {
  constructor(private _authService: BaseAuthService, private _navigationService: NavigationService) {}

  canActivate(_route: ActivatedRouteSnapshot, snapshot: RouterStateSnapshot): boolean {
    if (this._authService.isLoggedIn()) {
      // The patient is logged in, but they are trying to access a restricted route so redirect them to the /my-dental version of the same page
      this._navigationService.navigate(snapshot.url.replace("/restricted", "/my-dental"));

      return false;
    }

    if (!this._authService.isPatient()) {
      // The patient is not known and shouldn't be accessing any restricted routes so redirect to /login
      this._navigationService.navigate("/login");

      return false;
    }

    return true;
  }
}

function prepareRoutes(routes: Routes): Routes {
  return cloneDeep(routes).map((route) => {
    if (!route.redirectTo) {
      route.canActivate = route.canActivate || [];
      // Only add the auth guard if it doesnt have a redirect. Redirected routes are used to immediately reroute the navigation to another path before any component is instantiated or guards evaluated.
      route.canActivate.push(RestrictedAuthGuard);
    }

    return route;
  });
}

export const RESTRICTED_ROUTES = [
  {
    path: "access",
    loadComponent: loadComponentWithErrorHandling(() => import("./access/access.component").then((m) => m.AccessComponent)),
  },
  {
    path: "expired",
    loadComponent: loadComponentWithErrorHandling(() => import("./expired/expired.component").then((m) => m.ExpiredComponent)),
  },
  {
    path: "check-in",
    children: prepareRoutes(CHECK_IN_ROUTES),
  },
  {
    path: "medical-history",
    children: prepareRoutes([
      ...BASE_MEDICAL_HISTORY_ROUTES([MedicalHistoryAuthGuard]),
      {
        path: "medical-history-completed",
        component: MedicalHistoryCompletedComponent,
      },
    ]),
  },
  {
    path: "account",
    children: prepareRoutes(
      ACCOUNT_ROUTES.filter((childRoute) =>
        [Constants.ROUTES.ACCOUNT_BALANCE.last, Constants.ROUTES.ACCOUNT_PAYMENT_SUCCESS.last].includes(childRoute.path || "")
      )
    ),
  },
  {
    children: prepareRoutes(DEPOSITS_ROUTES),
    ...DEPOSITS_ROUTE_DEFAULTS,
  },
  {
    path: "login",
    children: prepareRoutes(((LOGIN_ROUTES && LOGIN_ROUTES[0]?.children) || []).filter((childRoute) => ["verify-short-code"].includes(childRoute.path || ""))),
  },
  {
    path: "**",
    redirectTo: "",
    canActivate: [RedirectGuard],
  },
];
